import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Schedule } from '@/api/scheduleApi';
import { Character } from '@/api/characterApi';
import { Clock, Users } from 'lucide-react';
import { getRaidAccentStyle } from '@/constants/raids';
import { useTheme } from '@/contexts/ThemeContext';
import ClassIconSimple from './ClassIconSimple';

interface MyParticipationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  schedules: Schedule[];
  characters: Character[];
}

interface CharacterParticipation {
  characterName: string;
  schedules: Schedule[];
}

export default function MyParticipationDialog({ open, onOpenChange, schedules, characters }: MyParticipationDialogProps) {
  const { theme } = useTheme();
  const [participationByCharacter, setParticipationByCharacter] = useState<CharacterParticipation[]>([]);

  /**
   * 캐릭터별로 참여 스케줄 그룹화 (레벨 높은 순으로 정렬)
   */
  useEffect(() => {
    if (!open) return;

    // 참여 중인 스케줄만 필터링
    const enrolledSchedules = schedules.filter(s => s.isEnrolled === 1);

    // 캐릭터별로 그룹화
    const groupedMap = new Map<string, Schedule[]>();

    enrolledSchedules.forEach(schedule => {
      const charName = schedule.enrolledCharacterName || '알 수 없음';

      if (!groupedMap.has(charName)) {
        groupedMap.set(charName, []);
      }
      groupedMap.get(charName)!.push(schedule);
    });

    // Map을 배열로 변환
    const grouped: CharacterParticipation[] = Array.from(groupedMap.entries()).map(([characterName, schedules]) => ({
      characterName,
      schedules: schedules.sort((a, b) => a.startTime.localeCompare(b.startTime)), // 시간순 정렬
    }));

    // 캐릭터를 레벨 높은 순으로 정렬
    const sortedGrouped = grouped.sort((a, b) => {
      const charA = characters.find(c => c.characterNickname === a.characterName);
      const charB = characters.find(c => c.characterNickname === b.characterName);

      if (!charA && !charB) return 0;
      if (!charA) return 1; // A가 없으면 뒤로
      if (!charB) return -1; // B가 없으면 A가 앞으로

      const levelA = parseFloat(charA.itemLevel.replace(',', ''));
      const levelB = parseFloat(charB.itemLevel.replace(',', ''));
      return levelB - levelA; // 레벨 높은 순
    });

    setParticipationByCharacter(sortedGrouped);
  }, [open, schedules, characters]);

  /**
   * 시간 포맷팅
   */
  const formatDateTime = (dateTimeStr: string) => {
    const date = new Date(dateTimeStr);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${month}/${day}(${dayOfWeek}) ${hours}:${minutes}`;
  };

  const totalParticipations = participationByCharacter.reduce((sum, cp) => sum + cp.schedules.length, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>내 참여 현황</DialogTitle>
          <DialogDescription>
            이번 주 참여 예정 레이드 ({totalParticipations}개)
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {participationByCharacter.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">참여 중인 일정이 없습니다.</p>
            </div>
          ) : (
            <Accordion type="single" collapsible className="w-full">
              {participationByCharacter.map((cp, index) => {
                // 캐릭터 정보에서 닉네임 찾기
                const characterInfo = characters.find(c => c.characterNickname === cp.characterName);
                const nickname = characterInfo?.characterNickname || '알 수 없음';

                return (
                  <AccordionItem key={index} value={cp.characterName}>
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3 w-full">
                        <ClassIconSimple
                          characterClass={characterInfo?.characterClass || cp.characterName}
                          classType={characterInfo?.classType as 'attacker' | 'supporter'}
                          size={32}
                        />
                        <div className="flex items-center gap-2 flex-1">
                          <span className="font-semibold">{nickname}</span>
                          {characterInfo && (
                            <span className="text-sm text-muted-foreground font-normal">
                              Lv.{characterInfo.itemLevel}
                            </span>
                          )}
                          <Badge variant="secondary" className="text-xs">
                            {cp.schedules.length}개 참여
                          </Badge>
                        </div>
                      </div>
                    </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-2 pt-2">
                      {cp.schedules.map((schedule) => {
                        const isDark = theme === 'dark';
                        const accentStyle = getRaidAccentStyle(schedule.raidTitle, isDark);
                        const isMySchedule = schedule.mySchedule === 1;

                        return (
                          <div
                            key={schedule.scheduleId}
                            className="border rounded-lg p-3 bg-card hover:bg-accent/5 transition-colors"
                            style={accentStyle}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-semibold text-sm">{schedule.raidTitle}</h4>
                              {isMySchedule && (
                                <Badge variant="secondary" className="text-xs">MY</Badge>
                              )}
                            </div>

                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              <div className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {formatDateTime(schedule.startTime)}
                              </div>
                              <div className="flex items-center gap-1">
                                <Users className="w-3.5 h-3.5" />
                                {schedule.enrollMemberNum}/{schedule.atkNum + schedule.supNum}
                              </div>
                            </div>

                            {schedule.content && (
                              <p className="text-xs text-muted-foreground mt-2 border-t pt-2">
                                📝 {schedule.content}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
            </Accordion>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
